import React, { useCallback, useMemo, useState } from 'react'
import Modal from 'LuxKit/Modal/Modal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import MaldivesDirectSearchFlightCalendar from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendar'
import MaldivesDirectSearchFlightCalendarMobile from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendarMobile'
import { useDateRangesByMonth, useOfferCharterCalendarData } from 'components/MaldivesDirect/MaldivesDirectSearchPage/useFlightCalendarData'
import { FlightsClassTypes } from 'constants/flight'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { MALDIVES_CHARTER_CONFIG } from 'constants/charterFlight'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { parseAsBoolean, useQueryState } from 'nuqs'

interface Props {
  selectedMonth: string
  onMonthChange: (month: string) => void
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  onDurationChange: (nights: number) => void
  durationOptions: ReadonlyArray<{ value: number, label: string }>
  offer: App.HotelOffer
  duration: number
  rooms: Array<App.Occupants>
  checkInDate?: string
  airport?: App.Airport
  multipleRoomBookingActive: boolean
  charterBundleRoute: App.CharterBundleRoute
}

/**
 * Charter Bundle Calendar Modal - Responsive
 * Renders desktop calendar on large screens, mobile calendar on small screens
 * Uses ModalContext pattern - resolves with selected dates when user selects
 */
function CharterBundleCalendarModal(props: Props) {
  const {
    offer,
    duration,
    rooms,
    checkInDate,
    multipleRoomBookingActive,
    charterBundleRoute,
    selectedMonth,
    onMonthChange,
    fareClass,
    priceMode,
    onDurationChange,
    durationOptions,
  } = props

  const modalElement = useModalElementContext<{ departDate: string, returnDate: string } | undefined>()
  const isMobileScreen = useScreenSizeOnly('mobile')

  const [localSelectedMonth, setLocalSelectedMonth] = useState(selectedMonth)

  // Read luxPlus state from URL (same as parent component)
  const [luxPlusEnabled, setLuxPlusEnabled] = useQueryState(
    'luxPlus',
    parseAsBoolean.withDefault(true)
  )

  // Generate date ranges based on current duration from context
  const dateRangesByMonth = useDateRangesByMonth(
    MALDIVES_CHARTER_CONFIG.DEPARTURE_WEEKDAY,
    MALDIVES_CHARTER_CONFIG.FUTURE_MONTHS_AMOUNT,
    duration
  )

  // Fetch calendar data based on current context values
  const flightCalendarData = useOfferCharterCalendarData({
    offer,
    nights: duration,
    rooms,
    fareClass,
    dateRangesByMonth,
    multipleRoomBookingActive,
    charterBundleRoute,
  })

  // Calculate selected option ID based on current checkIn date and duration
  const selectedOptionId = useMemo(() => {
    if (!checkInDate) return undefined
    return `${checkInDate}-${duration}n`
  }, [checkInDate, duration])

  const handleDateSelect = useCallback((option: { departDate: string, returnDate: string }) => {
    // Return the selected dates via modal's resolve mechanism
    modalElement.resolve(option)
  }, [modalElement])

  const handleLocalMonthChange = useCallback((month: string) => {
    // Update local state for immediate UI feedback
    setLocalSelectedMonth(month)
    // Also update parent state for consistency
    onMonthChange(month)
  }, [onMonthChange])

  const calendarProps = {
    onDateSelect: handleDateSelect,
    nights: duration,
    occupancies: rooms,
    luxPlusEnabled,
    onLuxPlusToggle: setLuxPlusEnabled,
    onDurationChange,
    durationOptions,
    itemBundleDataByDate: flightCalendarData.itemBundleDataByDate,
    dateRangesByMonth,
    selectedMonth: localSelectedMonth,
    onMonthChange: handleLocalMonthChange,
    fareClass,
    priceMode,
    selectedOptionId,
    mode: 'offer' as const,
  }

  return (
    <Modal
      title="Select your trip dates"
      size="XL"
      isPrimaryActionHidden
      onClose={() => modalElement.resolve(undefined)}
    >
      {isMobileScreen ? (
        <MaldivesDirectSearchFlightCalendarMobile {...calendarProps} />
      ) : (
        <MaldivesDirectSearchFlightCalendar {...calendarProps} />
      )}
    </Modal>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.offer! as App.HotelOffer,
  duration: state.duration,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  airport: state.airport,
  multipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
  charterBundleRoute: state.charterBundleRoute!,
}), CharterBundleCalendarModal)
